import get from 'lodash.get';
import { TFunction } from 'next-i18next';
import { ListingProperty } from '@/types/listing';
import {
  ComputedDataItem,
  ComputedDataSection,
  DataItem,
  DataSection,
  isDataItemWithValue,
} from './types';

export const isNonEmptyValue = (value: string | number | null | undefined) =>
  value !== null && value !== undefined && value !== '';

export const getComputedItem =
  (t: TFunction) =>
  ({ items: subItems, ...item }: DataItem, p: ListingProperty) => {
    const computed: ComputedDataItem = {
      ...item,
      label: item.label ? item.label : t(`generated_pdfs:${item.name}`),
      value: isDataItemWithValue(item)
        ? item.value(p)
        : (get(p, item.name) as string),
      description:
        typeof item.description === 'function' ? item.description(p) : null,
    };

    if (subItems) {
      const computedSubitems = subItems
        .map((r) => getComputedItem(t)(r, p))
        // only keep subitems that got a value
        .filter(({ value }) => isNonEmptyValue(value));
      // only add the subitems array if it has some items
      computed.items = computedSubitems.length ? computedSubitems : undefined;
    }

    return computed;
  };

/**
 * Computes the values for all sections in this panel,
 * removes any items that doesn't have a non-empty value,
 * and adds info about whether each section has any values.
 */
export const getComputedSection =
  (t: TFunction) =>
  (
    { title, items }: DataSection,
    property: ListingProperty,
  ): ComputedDataSection => {
    const computedItems = items
      .map((r) => getComputedItem(t)(r, property))
      .filter((r) => isNonEmptyValue(r.value));

    return {
      title,
      items: computedItems,
      hasValues: computedItems.length > 0,
    };
  };
